import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useMemo, useState } from "react";

import {
  getEntries,
  listPayPeriodApprovals,
  managerReviewPayPeriod,
  type Entry,
  type PayPeriodApproval,
} from "@/lib/api";
import { formatHours } from "@/lib/hoursMath";
import {
  computeTimecardPeriodStats,
  dayHasBreakdown,
  getDatesInPeriod,
  punchesByCalendarDay,
  weekdayShort,
} from "@/lib/timecardStats";
import { fmtPunchTime } from "@/lib/time";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";

/** Matches backend ManagerReviewBody.manager_note max_length. */
const MANAGER_NOTE_MAX_CHARS = 2000;

/** Short preview for list rows so long disputes do not stretch the Admin card. */
function noteSnippet(text: string | null | undefined, max = 80): string {
  const t = (text || "").replace(/\s+/g, " ").trim();
  if (!t) return "";
  if (t.length <= max) return t;
  return `${t.slice(0, max - 1)}…`;
}

/** Force long notes / unbroken strings to wrap inside dialog/card width. */
const wrapNoteClass =
  "text-xs text-foreground whitespace-pre-wrap break-words [overflow-wrap:anywhere] [word-break:break-word] max-w-full min-w-0";

/**
 * Manager / admin / readonly: queue of employee pay-period submissions needing review.
 * One Review action opens a dialog with Time Card–style hours breakdown + full note.
 */
export function ManagerPayPeriodApprovalsCard({
  enabled,
  canReview,
}: {
  enabled: boolean;
  canReview: boolean;
}) {
  const qc = useQueryClient();
  const [active, setActive] = useState<PayPeriodApproval | null>(null);
  const [managerNote, setManagerNote] = useState("");
  const [reviewMsg, setReviewMsg] = useState<string | null>(null);
  const [expandedDays, setExpandedDays] = useState<Set<string>>(new Set());

  const q = useQuery({
    queryKey: ["ppa-team", "pending_manager"],
    queryFn: () => listPayPeriodApprovals("pending_manager"),
    enabled,
    refetchInterval: 5 * 60 * 1000,
  });

  const hoursQ = useQuery({
    queryKey: [
      "ppa-review-hours",
      active?.user_id,
      active?.period_from,
      active?.period_to,
    ],
    queryFn: async () => {
      if (!active?.user_id || !active.period_from || !active.period_to) return [];
      const p = new URLSearchParams();
      p.set("user_id", String(active.user_id));
      p.set("from_date", `${active.period_from}T00:00:00`);
      p.set("to_date", `${active.period_to}T23:59:59`);
      p.set("limit", "500");
      return getEntries(p);
    },
    enabled: !!active?.user_id && !!active.period_from && !!active.period_to,
  });

  const stats = useMemo(
    () => computeTimecardPeriodStats(hoursQ.data ?? []),
    [hoursQ.data],
  );

  const punchesByDay = useMemo(
    () => punchesByCalendarDay(hoursQ.data ?? []),
    [hoursQ.data],
  );

  const periodDays = useMemo(() => {
    if (!active?.period_from || !active?.period_to) return [] as string[];
    return getDatesInPeriod(active.period_from, active.period_to);
  }, [active?.period_from, active?.period_to]);

  const reviewMut = useMutation({
    mutationFn: ({
      id,
      status,
      note,
    }: {
      id: number;
      status: "approved" | "rejected";
      note?: string;
    }) =>
      managerReviewPayPeriod(id, {
        manager_status: status,
        manager_note: note || undefined,
      }),
    onSuccess: (data) => {
      const sent = (data as { email_sent?: boolean })?.email_sent;
      setReviewMsg(
        sent === false
          ? "Saved. Email could not be sent (check Graph mail config)."
          : "Saved. Employee notified by email when mail is configured.",
      );
      void qc.invalidateQueries({ queryKey: ["ppa-team"] });
      setActive(null);
      setManagerNote("");
      setExpandedDays(new Set());
    },
    onError: (e: Error) => {
      setReviewMsg(e.message || "Review failed");
    },
  });

  const items = q.data?.approvals ?? [];
  const count = q.data?.count ?? 0;

  function openReview(a: PayPeriodApproval) {
    setActive(a);
    setManagerNote("");
    setReviewMsg(null);
    setExpandedDays(new Set());
  }

  function closeReview() {
    setActive(null);
    setManagerNote("");
    setExpandedDays(new Set());
  }

  function toggleDay(day: string) {
    setExpandedDays((prev) => {
      const next = new Set(prev);
      if (next.has(day)) next.delete(day);
      else next.add(day);
      return next;
    });
  }

  return (
    <>
      <Card className="min-w-0 overflow-hidden">
        <CardHeader>
          <CardTitle className="flex flex-wrap items-center gap-2">
            Pay period reviews
            {count > 0 ? (
              <Badge variant="destructive">{count} pending</Badge>
            ) : (
              <Badge variant="secondary">Clear</Badge>
            )}
          </CardTitle>
          <CardDescription>
            Employee submissions awaiting manager or admin review. Open Review to see the same daily
            hours breakdown as the Time Card, plus the full dispute/note, then approve or reject.
          </CardDescription>
        </CardHeader>
        <CardContent className="text-sm min-w-0">
          {q.isLoading && <div className="text-muted-foreground">Loading…</div>}
          {q.isError && (
            <div className="text-xs text-destructive">Failed to load approvals.</div>
          )}
          {!q.isLoading && count === 0 && (
            <div className="text-xs text-muted-foreground">No pending employee submissions.</div>
          )}
          {reviewMsg && !active && (
            <div className="mb-2 text-xs text-muted-foreground">{reviewMsg}</div>
          )}
          <ul className="space-y-2 min-w-0">
            {items.map((a) => {
              const snippet = noteSnippet(a.employee_note);
              const noteLabel =
                a.employee_status === "disputed"
                  ? "Dispute"
                  : a.employee_note
                    ? "Note"
                    : null;
              return (
                <li
                  key={a.id}
                  className="flex flex-wrap items-start justify-between gap-2 rounded border border-border px-3 py-2 text-xs min-w-0"
                >
                  <div className="min-w-0 flex-1 space-y-0.5 overflow-hidden">
                    <div className="font-medium truncate">
                      {a.full_name || a.email || `user ${a.user_id}`}
                      {a.pay_week ? ` · week ${a.pay_week}` : ""}
                    </div>
                    <div className="text-muted-foreground">
                      {a.period_from} → {a.period_to} · employee{" "}
                      <span className="font-medium">{a.employee_status}</span>
                    </div>
                    {noteLabel && snippet ? (
                      <div
                        className="text-muted-foreground truncate max-w-full"
                        title={a.employee_note || undefined}
                      >
                        <span className="font-medium text-foreground/80">{noteLabel}:</span>{" "}
                        {snippet}
                      </div>
                    ) : null}
                  </div>
                  {canReview && (
                    <div className="shrink-0">
                      <Button
                        type="button"
                        size="sm"
                        className="h-7 text-xs"
                        variant="outline"
                        onClick={() => openReview(a)}
                      >
                        Review
                      </Button>
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        </CardContent>
      </Card>

      <Dialog
        open={!!active}
        onOpenChange={(open) => {
          if (!open) closeReview();
        }}
      >
        <DialogContent
          className={[
            "w-[calc(100vw-1.5rem)] max-w-lg sm:max-w-xl",
            "max-h-[min(92vh,900px)]",
            "flex flex-col gap-3 overflow-x-hidden overflow-y-auto",
            "p-4 sm:p-6",
            "min-w-0",
          ].join(" ")}
        >
          <DialogHeader className="min-w-0 space-y-1.5 text-left pr-6 shrink-0">
            <DialogTitle>Review pay period</DialogTitle>
            <DialogDescription className="break-words [overflow-wrap:anywhere]">
              {active
                ? `${active.full_name || active.email || `User ${active.user_id}`} · ${active.period_from} → ${active.period_to}${
                    active.pay_week ? ` (week ${active.pay_week})` : ""
                  }`
                : ""}
            </DialogDescription>
          </DialogHeader>

          {active && (
            <div className="min-w-0 w-full space-y-3 text-sm overflow-x-hidden">
              <div className="flex flex-wrap gap-2 text-xs">
                <Badge
                  variant={active.employee_status === "disputed" ? "destructive" : "outline"}
                >
                  Employee: {active.employee_status}
                </Badge>
              </div>

              <div className="rounded border border-border p-3 space-y-1.5 min-w-0 w-full max-w-full overflow-hidden">
                <div className="text-sm font-medium">
                  {active.employee_status === "disputed"
                    ? "Employee dispute"
                    : "Employee note"}
                </div>
                {active.employee_note?.trim() ? (
                  <div className={`${wrapNoteClass} rounded bg-muted/40 p-2`}>
                    {active.employee_note}
                  </div>
                ) : (
                  <div className="text-xs text-muted-foreground">
                    {active.employee_status === "disputed"
                      ? "No dispute text was provided."
                      : "No optional note on this approval."}
                  </div>
                )}
              </div>

              {/* Time Card–style daily hours + expandable customer/cost code */}
              <div className="rounded border border-border p-3 space-y-2 min-w-0 w-full overflow-hidden">
                <div className="text-sm font-medium">
                  Hours for this employee (same breakdown as Time Card)
                </div>
                {hoursQ.isLoading && (
                  <div className="text-xs text-muted-foreground">Loading punches…</div>
                )}
                {hoursQ.isError && (
                  <div className="text-xs text-destructive">
                    Could not load entries for this period.
                  </div>
                )}
                {!hoursQ.isLoading && !hoursQ.isError && (
                  <>
                    <div className="grid grid-cols-3 gap-2 text-center min-w-0">
                      <div className="rounded border p-2 min-w-0">
                        <div className="text-[10px] text-muted-foreground">Total</div>
                        <div className="text-lg font-semibold tabular-nums">
                          {formatHours(stats.totalHours)}
                        </div>
                      </div>
                      <div className="rounded border p-2 min-w-0">
                        <div className="text-[10px] text-muted-foreground">Regular</div>
                        <div className="text-lg font-semibold tabular-nums">
                          {formatHours(stats.totals.regular)}
                        </div>
                      </div>
                      <div className="rounded border p-2 min-w-0">
                        <div className="text-[10px] text-muted-foreground">OT1</div>
                        <div className="text-lg font-semibold tabular-nums">
                          {formatHours(stats.totals.ot1)}
                        </div>
                      </div>
                    </div>

                    {periodDays.length === 0 ? (
                      <div className="text-xs text-muted-foreground">No period dates.</div>
                    ) : (
                      <div className="w-full min-w-0 overflow-x-auto border rounded">
                        <table className="w-full min-w-[280px] text-xs">
                          <thead>
                            <tr className="border-b bg-muted/50">
                              <th className="text-left p-2">Day</th>
                              <th className="text-left p-2">Date</th>
                              <th className="text-right p-2">Hours</th>
                              <th className="w-6 p-2" />
                            </tr>
                          </thead>
                          <tbody>
                            {periodDays.map((day) => {
                              const dayData = stats.byDay[day] || {
                                total: 0,
                                byCustomer: {},
                              };
                              const isExpanded = expandedDays.has(day);
                              const hasBreakdown = dayHasBreakdown(dayData);
                              return (
                                <FragmentDay
                                  key={day}
                                  day={day}
                                  dayName={weekdayShort(day)}
                                  total={dayData.total}
                                  hasBreakdown={hasBreakdown}
                                  punches={punchesByDay[day] ?? []}
                                  isExpanded={isExpanded}
                                  byCustomer={dayData.byCustomer}
                                  onToggle={() => toggleDay(day)}
                                />
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    )}
                    <p className="text-[10px] text-muted-foreground break-words">
                      Click + on a day to expand customer / cost code. Closed punches only; weekly OT
                      after 40h (Sun–Sat); CA sites daily OT after 8h.
                    </p>
                  </>
                )}
              </div>

              <div className="grid gap-1 min-w-0 w-full">
                <div className="flex items-baseline justify-between gap-2 min-w-0">
                  <Label className="text-xs text-muted-foreground" htmlFor="ppa-manager-note">
                    Manager note (optional; recommended on reject)
                  </Label>
                  <span
                    className={`text-[10px] tabular-nums shrink-0 ${
                      managerNote.length >= MANAGER_NOTE_MAX_CHARS * 0.9
                        ? "text-destructive font-medium"
                        : "text-muted-foreground"
                    }`}
                  >
                    {managerNote.length}/{MANAGER_NOTE_MAX_CHARS}
                  </span>
                </div>
                <Textarea
                  id="ppa-manager-note"
                  className="min-h-[72px] max-w-full min-w-0 w-full text-sm resize-y break-words"
                  value={managerNote}
                  maxLength={MANAGER_NOTE_MAX_CHARS}
                  rows={3}
                  onChange={(e) =>
                    setManagerNote(e.target.value.slice(0, MANAGER_NOTE_MAX_CHARS))
                  }
                  placeholder="Included in the employee email when set"
                />
              </div>

              {reviewMsg && (
                <div className="text-xs text-muted-foreground break-words">{reviewMsg}</div>
              )}
            </div>
          )}

          <DialogFooter className="gap-2 sm:gap-0 shrink-0 min-w-0 flex-wrap">
            <Button type="button" variant="outline" onClick={closeReview}>
              Cancel
            </Button>
            {canReview && active && (
              <>
                <Button
                  type="button"
                  variant="outline"
                  disabled={reviewMut.isPending}
                  onClick={() => {
                    setReviewMsg(null);
                    reviewMut.mutate({
                      id: active.id,
                      status: "rejected",
                      note: managerNote,
                    });
                  }}
                >
                  Reject &amp; notify
                </Button>
                <Button
                  type="button"
                  disabled={reviewMut.isPending}
                  onClick={() => {
                    setReviewMsg(null);
                    reviewMut.mutate({
                      id: active.id,
                      status: "approved",
                      note: managerNote,
                    });
                  }}
                >
                  Approve &amp; notify
                </Button>
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

/** One day row + optional expanded customer/cost-code breakdown (Time Card style). */
function FragmentDay({
  day,
  dayName,
  total,
  hasBreakdown,
  punches,
  isExpanded,
  byCustomer,
  onToggle,
}: {
  day: string;
  dayName: string;
  total: number;
  hasBreakdown: boolean;
  punches: Entry[];
  isExpanded: boolean;
  byCustomer: Record<string, { total: number; byCostCode: Record<string, number> }>;
  onToggle: () => void;
}) {
  const expandable = hasBreakdown || punches.length > 0;
  return (
    <>
      <tr
        className={`border-b last:border-0 ${expandable ? "cursor-pointer hover:bg-muted/30" : ""}`}
        onClick={expandable ? onToggle : undefined}
      >
        <td className="p-2">{dayName}</td>
        <td className="p-2 font-mono">{day}</td>
        <td className="p-2 text-right tabular-nums">{formatHours(total)}</td>
        <td className="p-2 text-center text-xs">
          {expandable ? (isExpanded ? "−" : "+") : ""}
        </td>
      </tr>
      {isExpanded && expandable && (
        <tr>
          <td colSpan={4} className="p-0 bg-muted/10">
            <div className="pl-6 pr-2 py-1 text-xs min-w-0 max-w-full overflow-hidden">
              {punches.map((entry) => (
                <div key={entry.id} className="flex justify-between gap-2 py-0.5 min-w-0">
                  <span className="min-w-0 break-words [overflow-wrap:anywhere]">
                    In {fmtPunchTime(entry.recorded_time, entry.tz_name).primary}
                    {" · Out "}
                    {fmtPunchTime(entry.clock_out_time, entry.tz_name, entry.clock_out_tz_name).primary}
                    {" · "}
                    {entry.customer_name || entry.site_name || "Unspecified"}
                    {" · "}
                    {entry.cost_code_name || "Unspecified"}
                  </span>
                  <span className="shrink-0 tabular-nums">{formatHours(entry.duration)}</span>
                </div>
              ))}
              {hasBreakdown && Object.entries(byCustomer || {}).map(([cust, custData]) => (
                <div key={cust} className="mb-1 min-w-0">
                  <div className="font-medium text-muted-foreground break-words [overflow-wrap:anywhere]">
                    {cust} — {formatHours(custData.total || 0)} h
                  </div>
                  {Object.entries(custData.byCostCode || {}).map(([cc, h]) => (
                    <div
                      key={cc}
                      className="flex justify-between gap-2 pl-3 py-0.5 min-w-0"
                    >
                      <span className="min-w-0 break-words [overflow-wrap:anywhere]">{cc}</span>
                      <span className="shrink-0 tabular-nums">{formatHours(h || 0)}</span>
                    </div>
                  ))}
                </div>
              ))}
            </div>
          </td>
        </tr>
      )}
    </>
  );
}
